import { useForm } from '@tanstack/react-form';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../common/Modal';

/** Ties the label to the textarea. */
const REASON_FIELD_ID = 'reject-reason';

export interface RejectDialogProps {
  open: boolean;
  submitterName: string | null;
  onConfirm: (reason: string | undefined) => void;
  onCancel: () => void;
}

/**
 * Reject-with-a-reason dialog. The body below is mounted only while open, so
 * each opening starts from a blank form and an autofocused field — no effect
 * has to clear the previous reason or chase the textarea afterwards.
 */
export function RejectDialog({ open, ...props }: RejectDialogProps) {
  if (!open) {
    return null;
  }
  return <RejectDialogBody {...props} />;
}

function RejectDialogBody({
  submitterName,
  onConfirm,
  onCancel,
}: Omit<RejectDialogProps, 'open'>) {
  const { t } = useTranslation();
  const titleId = useId();
  const form = useForm({
    defaultValues: { reason: '' },
    onSubmit: ({ value }) => {
      const trimmed = value.reason.trim();
      onConfirm(trimmed.length > 0 ? trimmed : undefined);
    },
  });

  // Escape and a click beside the form cancel: the native <dialog> closes and
  // Modal reports it. Focus then goes back to the Reject button.
  return (
    <Modal
      aria-labelledby={titleId}
      onClose={onCancel}
      className="backdrop:bg-ink/40"
    >
      <form
        className="w-full max-w-md rounded-xl bg-card p-6 text-ink"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <h3 id={titleId} className="mb-1 text-base font-semibold text-ink">
          {t('admin.moderation.rejectDialogTitle')}
        </h3>
        {submitterName && (
          <p className="mb-4 text-sm text-muted">
            {t('admin.moderation.submittedBy')} {submitterName}
          </p>
        )}
        <label
          className="mb-1 block text-xs font-medium text-ink-soft"
          htmlFor={REASON_FIELD_ID}
        >
          {t('admin.moderation.rejectPrompt')}
        </label>
        <form.Field name="reason">
          {(field) => (
            <>
              <textarea
                id={REASON_FIELD_ID}
                // react-doctor-disable-next-line react-doctor/no-autofocus -- in a modal <dialog>, autofocus is how the opening focus is chosen (the browser's dialog focusing steps)
                autoFocus
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                maxLength={500}
                rows={3}
                className="w-full rounded-lg border border-rule px-3 py-2 text-sm focus:border-blue"
                placeholder={t('admin.moderation.rejectReasonPlaceholder')}
              />
              <p className="mt-1 text-xs text-muted-soft">
                {field.state.value.length} / 500
              </p>
            </>
          )}
        </form.Field>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-blue/5"
          >
            {t('admin.moderation.cancel')}
          </button>
          <button
            type="submit"
            className="rounded-lg bg-magenta px-3 py-1.5 text-xs font-medium text-ink hover:bg-magenta/85"
          >
            {t('admin.moderation.confirmReject')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
